import { useEffect } from 'react';
import titleManager from '../utils/title-manager';

/**
 * Custom hook for managing page titles and meta tags
 * @param {Object} options - Configuration options
 * @param {string} options.title - Page title
 * @param {string} options.description - Meta description
 * @param {string} options.image - Open Graph/Twitter image URL
 * @param {string} options.url - Canonical URL
 * @param {boolean} options.suffix - Whether to append site name to title (default: true)
 * @param {boolean} options.setOpenGraph - Whether to set Open Graph tags (default: true)
 * @param {boolean} options.setTwitterCard - Whether to set Twitter Card tags (default: true)
 */
export const usePageTitle = ({
  title,
  description,
  image,
  url,
  suffix = true,
  setOpenGraph = true,
  setTwitterCard = true
}) => {
  useEffect(() => {
    // Set title
    if (title) {
      titleManager.setTitle(title, suffix);
    }

    // Set description
    if (description) {
      titleManager.setDescription(description);
    }

    // Set canonical URL
    if (url) {
      titleManager.setCanonical(url);
    }

    // Set Open Graph tags
    if (setOpenGraph && (title || description || image || url)) {
      titleManager.setOpenGraph({
        title: title || titleManager.defaultTitle,
        description: description || titleManager.defaultDescription,
        image,
        url
      });
    }

    // Set Twitter Card tags
    if (setTwitterCard && (title || description || image)) {
      titleManager.setTwitterCard({
        title: title || titleManager.defaultTitle,
        description: description || titleManager.defaultDescription,
        image
      });
    }

    // Cleanup function to reset title when component unmounts
    return () => {
      // Only reset if this is the last component using the title manager
      // In a real app, you might want more sophisticated cleanup logic
    };
  }, [title, description, image, url, suffix, setOpenGraph, setTwitterCard]);
};

/**
 * Simple hook for just setting the page title
 * @param {string} title - Page title
 * @param {boolean} suffix - Whether to append site name (default: true)
 */
export const useTitle = (title, suffix = true) => {
  useEffect(() => {
    if (title) {
      titleManager.setTitle(title, suffix);
    }
  }, [title, suffix]);
};

export default usePageTitle; 